import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  XMarkIcon, 
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon,
  CheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { environment } from './environment';
import { getToken } from './App';
import { getVisibleFieldsForEmrType, renderFieldValue, getFieldIcon, formatFieldName } from './utils';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Session data state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Generate sessions state
  const [generatingSessions, setGeneratingSessions] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);

  // Feedback state
  const [feedback, setFeedback] = useState('');
  const [tempFeedback, setTempFeedback] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Field display configuration
  const [fieldDisplayConfig, setFieldDisplayConfig] = useState({});

  // Documentation method popup state
  const [showDocumentationMethodModal, setShowDocumentationMethodModal] = useState(false);
  const [selectedDocumentationMethod, setSelectedDocumentationMethod] = useState(null);

  // EMR Type Results and Manual Fields for dynamic field display
  const [emrTypeResults, setEmrTypeResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [manualFields, setManualFields] = useState([]);
  const [loadingManualFields, setLoadingManualFields] = useState(false);

  // Helper function to check if a value is meaningful (not null, empty, or "None")
  const hasMeaningfulContent = (value) => {
    return value && 
           value !== 'None' && 
           value !== 'none' && 
           value !== 'null' && 
           value !== 'NULL' && 
           value.trim() !== '';
  };

  // Helper function to validate AI response format
  const validateAIResponse = (responseData) => {
    if (!responseData) return false;
    
    // Check if response has the expected structure with clear section headers
    const responseText = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
    
    // Look for clear section indicators - ONLY accept responses with "Section X:" format
    const hasSection1 = /Section\s*1[:\s]*.*?(?:Methods|methods)/i.test(responseText);
    const hasSection2 = /Section\s*2[:\s]*.*?(?:Progress|progress).*?(?:Goal|goal)/i.test(responseText);
    const hasSection3 = /Section\s*3[:\s]*.*?(?:Recommended|recommended).*?(?:Changes|changes)/i.test(responseText);
    
    // Response is valid ONLY if it has clear section headers
    const hasClearSections = hasSection1 && hasSection2 && hasSection3;
    
    return hasClearSections;
  };

  // State for malformed response detection
  const [showRegenerationPrompt, setShowRegenerationPrompt] = useState(false);
  const [malformedResponseData, setMalformedResponseData] = useState(null);

  // Check if any response field has data to determine button text
  const hasExistingResponses = () => {
    if (!session) return false;
    
    return !!(
      hasMeaningfulContent(session.methods_response) || 
      hasMeaningfulContent(session.progress_towards_goal_response) || 
      hasMeaningfulContent(session.recommended_changes_response)
    );
  };

  // Handle Generate Sessions
  const handleGenerateSessions = useCallback(async () => {
    if (!sessionId) return;
    
    setGeneratingSessions(true);
    setGenerateError('');
    
    try {
      const response = await fetch(`${environment.apiUrl}/sessions/${sessionId}/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Generated sessions data:', data);
        
        // Validate the AI response format
        const isValidResponse = validateAIResponse(data);
        
        if (!isValidResponse) {
          console.warn('AI response format is malformed, showing regeneration prompt');
          setMalformedResponseData(data);
          setShowRegenerationPrompt(true);
        } else {
          console.log('AI response format is valid');
        }
        
        // Refresh the session data to get the updated database values
        const sessionResponse = await fetch(`${environment.apiUrl}/sessions/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
          },
        });
        
        if (sessionResponse.ok) {
          const updatedSession = await sessionResponse.json();
          setSession(updatedSession);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to generate sessions:', errorData);
        setGenerateError(errorData.detail || 'Failed to generate sessions');
      }
    } catch (err) {
      console.error('Network error generating sessions:', err);
      setGenerateError('Network error generating sessions');
    } finally {
      setGeneratingSessions(false);
      setIsAutoGenerating(false);
    }
  }, [sessionId]);

  // Load field display configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('fieldDisplayConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setFieldDisplayConfig(config);
        console.log('=== SESSION DETAIL FIELD CONFIG LOADED ===');
        console.log('Loaded config:', config);
        console.log('Session emr_name:', session?.emr_name);
        console.log('Visible fields for this session:', getVisibleFieldsForEmrType(session?.emr_name || 'Unknown', config));
        console.log('==========================================');
      } catch (error) {
        console.error('Error parsing field display config:', error);
      }
    }
  }, [session]);

  // Fetch EMR Type Results for dynamic field generation
  useEffect(() => {
    const fetchEmrTypeResults = async () => {
      if (!session?.emr_type_id) return;
      
      setLoadingResults(true);
      try {
        const response = await fetch(`${environment.apiUrl}/results`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setEmrTypeResults(data);
        }
      } catch (err) {
        console.error('Error fetching EMR type results:', err);
      } finally {
        setLoadingResults(false);
      }
    };

    fetchEmrTypeResults();
  }, [session?.emr_type_id]);

  // Fetch Manual Fields
  useEffect(() => {
    const fetchManualFields = async () => {
      if (!session?.emr_type_id) return;
      
      setLoadingManualFields(true);
      try {
        const response = await fetch(`${environment.apiUrl}/manual-fields/`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setManualFields(data);
        }
      } catch (err) {
        console.error('Error fetching manual fields:', err);
      } finally {
        setLoadingManualFields(false);
      }
    };

    fetchManualFields();
  }, [session?.emr_type_id]);

  // Get all fields (dynamic + manual) for the session's EMR type
  const getAllFieldsForSessionEmrType = () => {
    if (!session?.emr_type_id) {
      // Fallback to default fields if no EMR type
      return ['Type', 'Client', 'Instructions', 'Created At'];
    }

    const dynamicFields = [];
    const manualFieldsList = [];

    // Get dynamic fields from emrTypeResults
    if (emrTypeResults && Array.isArray(emrTypeResults)) {
      const confirmedFields = emrTypeResults.filter(result => 
        result.emr_type_id === session.emr_type_id && result.status === "confirmed"
      );
      dynamicFields.push(...confirmedFields.map(result => result.key));
    }

    // Get manual fields
    if (manualFields && Array.isArray(manualFields)) {
      const typeManualFields = manualFields.filter(field => 
        field.emr_type_id === session.emr_type_id
      );
      manualFieldsList.push(...typeManualFields.map(field => field.name));
    }

    // Combine with common fields
    const commonFields = ['Type', 'Client', 'Instructions', 'Created At'];
    const allFields = [...commonFields, ...dynamicFields, ...manualFieldsList];
    const uniqueFields = [...new Set(allFields)];

    console.log('All fields for session EMR type:', session.emr_type_id, {
      dynamicFields,
      manualFields: manualFieldsList,
      allFields: uniqueFields
    });

    return uniqueFields;
  };

  // Fetch session details
  useEffect(() => {
    const fetchSessionDetails = async () => {
      try {
        const response = await fetch(`${environment.apiUrl}/sessions/${sessionId}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        if (response.ok) {
          const sessionData = await response.json();
          console.log('Session details fetched:', sessionData);
          console.log('Session data keys:', Object.keys(sessionData));
          console.log('client_id_name field:', sessionData.client_id_name);
          console.log('client_id field:', sessionData.client_id);
          setSession(sessionData);

          // Load existing feedback if it exists
          if (sessionData.feedback) {
            console.log('Found existing feedback:', sessionData.feedback);
            setFeedback(sessionData.feedback);
          }

          // Auto-generate responses ONLY for newly created sessions
          // Check if this is a new session by looking at location state or URL params
          const isNewSession = location.state?.isNewSession || 
                              new URLSearchParams(location.search).get('new') === 'true' ||
                              (!hasMeaningfulContent(sessionData.methods_response) && 
                               !hasMeaningfulContent(sessionData.progress_towards_goal_response) && 
                               !hasMeaningfulContent(sessionData.recommended_changes_response) &&
                               sessionData.created_at && 
                               new Date(sessionData.created_at) > new Date(Date.now() - 5 * 60 * 1000)); // Created within last 5 minutes
          
          if (isNewSession) {
            console.log('New session detected - auto-generating responses...');
            setIsAutoGenerating(true);
            // Small delay to ensure UI is ready
            setTimeout(() => {
              handleGenerateSessions();
            }, 500);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to fetch session details:', errorData);
          setError(errorData.detail || 'Failed to fetch session details');
        }
      } catch (err) {
        console.error('Network error fetching session details:', err);
        setError('Network error fetching session details');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchSessionDetails();
    }
  }, [sessionId, handleGenerateSessions]);

  // Helper functions

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      // Check if it's a date-only string (YYYY-MM-DD) or datetime string
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Date-only string like "2025-06-01" - add time to avoid timezone issues
        date = new Date(dateString + 'T00:00:00');
      } else {
        // DateTime string like "2025-06-27 15:41:55" - parse directly
        date = new Date(dateString);
      }
      
      if (isNaN(date)) return dateString;
      
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      // Remove the word "at" from the formatted date string
      return formattedDate.replace(' at ', ' ');
    } catch (error) {
      return dateString;
    }
  };

  // Handle regeneration prompt
  const handleRegenerateFromPrompt = () => {
    setShowRegenerationPrompt(false);
    setMalformedResponseData(null);
    handleGenerateSessions();
  };

  const handleDismissRegenerationPrompt = () => {
    setShowRegenerationPrompt(false);
    setMalformedResponseData(null);
  };

  // Handle feedback editing
  const handleStartEditingFeedback = () => {
    // Only set meaningful content as temp feedback, otherwise start with empty string
    setTempFeedback(hasMeaningfulContent(feedback) ? feedback : '');
    setShowFeedbackModal(true);
  };


  const handleSaveAndRegenerate = async () => {
    try {
      // First save the feedback
      const response = await fetch(`${environment.apiUrl}/sessions/${sessionId}/feedback`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedback: tempFeedback })
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(data.feedback);
        setShowFeedbackModal(false);
        console.log('Feedback saved successfully:', data);
        
        // Then trigger regeneration
        await handleGenerateSessions();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save feedback:', errorData);
        // You could add error state handling here if needed
      }
    } catch (error) {
      console.error('Network error saving feedback:', error);
      // You could add error state handling here if needed
    }
  };

  const handleCancelFeedback = () => {
    // Reset temp feedback to the original meaningful content (or empty if none)
    setTempFeedback(hasMeaningfulContent(feedback) ? feedback : '');
    setShowFeedbackModal(false);
  };

  // Click handlers for data source links
  const handleClientHistoryClick = () => {
    if (session?.client_id) {
      navigate(`/Clients/${session.client_id}`);
    }
  };

  const handleTypeWritingClick = () => {
    navigate('/profile');
  };

  const handleUserSessionInstructionsClick = () => {
    navigate('/profile');
  };

  const handleCustomInstructionsClick = () => {
    // Scroll to the custom instructions section (we'll add an ID to that section)
    const customInstructionsSection = document.getElementById('custom-instructions-section');
    if (customInstructionsSection) {
      customInstructionsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleEmrTypeInstructionsClick = async () => {
    if (!session) return;
    
    try {
      // Check if it's a default documentation method
      if (session.is_default_documentation_method) {
        // If it's default, redirect to the EMR Type details page with showInstructions parameter
        if (session.emr_type_id) {
          navigate(`/emr-types/${session.emr_type_id}?showInstructions=true`);
        } else {
          console.error('No emr_type_id found for default documentation method');
          // Fallback to settings page
          navigate('/settings?tab=documentation-methods');
        }
      } else {
        // If it's not default, show the documentation method popup
        if (session.documentation_method_id) {
          // Fetch the documentation method details and show in popup
          const response = await fetch(`${environment.apiUrl}/documentation-methods/${session.documentation_method_id}`, {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          });

          if (response.ok) {
            const documentationMethod = await response.json();
            // Show the documentation method in a popup
            setSelectedDocumentationMethod(documentationMethod);
            setShowDocumentationMethodModal(true);
          } else {
            console.error('Failed to fetch documentation method');
            navigate('/settings?tab=documentation-methods');
          }
        } else {
          console.error('No documentation_method_id found');
          navigate('/settings?tab=documentation-methods');
        }
      }
    } catch (err) {
      console.error('Error handling EMR Type Instructions click:', err);
      // Fallback to settings page
      navigate('/settings?tab=documentation-methods');
    }
  };


  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-slate-700">Loading session details...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-red-600 text-lg mb-4">Error loading session details: {error}</div>
            <button
              onClick={() => navigate('/sessions')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No session found
  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-slate-600 text-lg mb-4">Session not found</div>
            <button
              onClick={() => navigate('/sessions')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => {
              // Check if user came from a client page
              const referrer = location.state?.from || document.referrer;
              if (referrer && referrer.includes('/Clients/')) {
                // Extract client ID from referrer or state
                const clientId = location.state?.clientId || referrer.split('/Clients/')[1]?.split('?')[0];
                if (clientId) {
                  navigate(`/Clients/${clientId}`);
                } else {
                  navigate('/sessions');
                }
              } else {
                navigate('/sessions');
              }
            }}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to {location.state?.from?.includes('/Clients/') ? 'Client' : 'Sessions'}
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Session Details</h1>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleGenerateSessions}
                disabled={generatingSessions}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingSessions ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isAutoGenerating ? 'Auto-generating...' : 'Generating...'}
                  </div>
                ) : (
                  hasExistingResponses() ? 'Regenerate Sessions' : 'Generate Sessions'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Session Response Information */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">How Session Responses Are Generated</h3>
                <p className="text-slate-600 leading-relaxed">
                  The session responses are intelligently generated based on multiple data sources to provide comprehensive and personalized insights:
                </p>
                <div className="mt-3 text-sm text-slate-600">
                  <div className="flex flex-wrap items-center gap-4">
                    <button 
                      onClick={handleClientHistoryClick}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                      disabled={!session?.client_id}
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors"></span>
                      <strong className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Client History:</strong>
                    </button>
                    <button 
                      onClick={handleTypeWritingClick}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors"></span>
                      <strong className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Type Writing:</strong>
                    </button>
                    <button 
                      onClick={handleUserSessionInstructionsClick}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors"></span>
                      <strong className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">User Session Instructions:</strong>
                    </button>
                    <button 
                      onClick={handleCustomInstructionsClick}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors"></span>
                      <strong className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">Custom Instructions from that session:</strong>
                    </button>
                    <button 
                      onClick={handleEmrTypeInstructionsClick}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 transition-colors"></span>
                      <strong className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">EMR Type Instructions:</strong>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Session Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Side - Session Information */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Session Info */}
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-slate-200 rounded-xl p-8">
              <div className="flex items-start gap-8">
                {/* Session Icon */}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-extrabold text-3xl shadow-lg">
                  {session.emr_name?.charAt(0) || 'S'}
                </div>
                
                {/* Session Details */}
                <div className="flex-1">
                  <h2 className="text-4xl font-bold text-slate-900 mb-2">
                    {session.emr_name} Session
                  </h2>
                  <p className="text-slate-500 text-lg mb-6">Session Information</p>
                  
                  {/* Dynamic Session Information Box */}
                  <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-slate-100">
                    <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
                      <ClipboardDocumentIcon className="w-8 h-8 text-black" />
                      Session Information
                    </h3>
                    <div className="space-y-4">
                      {getAllFieldsForSessionEmrType().map((fieldName) => {
                        const fieldValue = renderFieldValue(session, fieldName, formatDate);
                        const iconName = getFieldIcon(fieldName);
                        
                        // Get the appropriate icon component
                        let IconComponent = DocumentTextIcon; // default
                        switch (iconName) {
                          case 'UserIcon':
                            IconComponent = UserIcon;
                            break;
                          case 'CalendarIcon':
                            IconComponent = CalendarIcon;
                            break;
                          case 'DocumentTextIcon':
                          default:
                            IconComponent = DocumentTextIcon;
                            break;
                        }
                        
                        return (
                          <div key={fieldName} className="flex items-start gap-4">
                            <IconComponent className="w-6 h-6 text-black mt-1" />
                            <div>
                              <p className="text-sm font-bold text-slate-900">{formatFieldName(fieldName)}</p>
                              <p className="text-slate-500 font-normal whitespace-pre-wrap">{fieldValue || ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Instructions Section */}
            <div id="custom-instructions-section" className="bg-gradient-to-br from-purple-50 via-white to-pink-50 border border-purple-200 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-3">
                <DocumentTextIcon className="w-8 h-8 text-purple-600" />
                Custom Instructions from this Session
              </h3>
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-6 border border-purple-100">
                <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                  {session?.manual_instructions || ''}
                </p>
              </div>
            </div>

          </div>

          {/* Right Side - 3 Big Boxes */}
          <div className="space-y-6">
            {/* Box 1 - Methods Response */}
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 h-48 overflow-y-auto">
              {generateError ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-red-600">
                    <span className="text-xl mb-2">⚠️</span>
                    <p className="text-sm">Error: {generateError}</p>
                  </div>
                </div>
              ) : generatingSessions ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-blue-600">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">Generating...</p>
                  </div>
                </div>
              ) : hasMeaningfulContent(session?.methods_response) ? (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    Methods Response
                  </h4>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap overflow-hidden">
                    {session?.methods_response}
                  </div>
                </div>
              ) : (
                <div className="text-center h-full flex items-center justify-center">
                  <div>
                    <p className="text-xs text-slate-400 mt-1">Click "{hasExistingResponses() ? 'Regenerate' : 'Generate'} Sessions" to populate</p>
                  </div>
                </div>
              )}
            </div>

            {/* Box 2 - Progress Towards Goal Response */}
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 h-48 overflow-y-auto">
              {generateError ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-red-600">
                    <span className="text-xl mb-2">⚠️</span>
                    <p className="text-sm">Error: {generateError}</p>
                  </div>
                </div>
              ) : generatingSessions ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-blue-600">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">Generating...</p>
                  </div>
                </div>
              ) : hasMeaningfulContent(session?.progress_towards_goal_response) ? (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    Progress Towards Goal Response
                  </h4>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap overflow-hidden">
                    {session?.progress_towards_goal_response}
                  </div>
                </div>
              ) : (
                <div className="text-center h-full flex items-center justify-center">
                  <div>
                    <p className="text-xs text-slate-400 mt-1">Click "{hasExistingResponses() ? 'Regenerate' : 'Generate'} Sessions" to populate</p>
                  </div>
                </div>
              )}
            </div>

            {/* Box 3 - Recommended Changes Response */}
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 h-48 overflow-y-auto">
              {generateError ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-red-600">
                    <span className="text-xl mb-2">⚠️</span>
                    <p className="text-sm">Error: {generateError}</p>
                  </div>
                </div>
              ) : generatingSessions ? (
                <div className="text-center h-full flex items-center justify-center">
                  <div className="text-blue-600">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">Generating...</p>
                  </div>
                </div>
              ) : hasMeaningfulContent(session?.recommended_changes_response) ? (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    Recommended Changes Response
                  </h4>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap overflow-hidden">
                    {session?.recommended_changes_response}
                  </div>
                </div>
              ) : (
                <div className="text-center h-full flex items-center justify-center">
                  <div>
                    <p className="text-xs text-slate-400 mt-1">Click "{hasExistingResponses() ? 'Regenerate' : 'Generate'} Sessions" to populate</p>
                  </div>
                </div>
              )}
            </div>

            {/* Box 4 - Feedback */}
            <div className="bg-white border-2 border-slate-200 rounded-xl p-4 min-h-32">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800">Feedback</h4>
                  <button
                    onClick={handleStartEditingFeedback}
                    className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex-1">
                  <div 
                    className="text-sm text-slate-600 cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors break-words whitespace-pre-wrap"
                    onClick={handleStartEditingFeedback}
                    title={hasMeaningfulContent(feedback) ? feedback : ''}
                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  >
                    {hasMeaningfulContent(feedback) ? feedback : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Method Modal */}
      {showDocumentationMethodModal && selectedDocumentationMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-800">Documentation Method</h2>
              <button
                onClick={() => setShowDocumentationMethodModal(false)}
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Name
                </label>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-lg font-medium text-slate-900">
                    {selectedDocumentationMethod.name}
                  </span>
                </div>
              </div>

              {/* Session Instructions */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Session Instructions
                </label>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-slate-700 font-mono text-sm leading-relaxed">
                    {selectedDocumentationMethod.session_instructions}
                  </pre>
                </div>
              </div>

              {/* Created Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Created
                </label>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-slate-700">
                    {new Date(selectedDocumentationMethod.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end mt-8 pt-6 border-t border-slate-200">
              <button
                onClick={() => setShowDocumentationMethodModal(false)}
                className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regeneration Prompt Modal */}
      {showRegenerationPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-800">
                  AI Response Format Issue
                </h3>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-600 mb-3">
                  The AI response doesn't follow the expected format with clear section headers. 
                  This can cause the response boxes to appear empty.
                </p>
                <p className="text-sm text-slate-500">
                  Would you like to regenerate the response to get a properly formatted result?
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRegenerateFromPrompt}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Regenerate Response
                </button>
                <button
                  onClick={handleDismissRegenerationPrompt}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-800">Edit Feedback</h2>
              <button
                onClick={handleCancelFeedback}
                className="p-2 text-red-500 hover:text-red-700 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 min-h-0 flex flex-col">
              <textarea
                value={tempFeedback}
                onChange={(e) => setTempFeedback(e.target.value)}
                placeholder="Enter feedback..."
                className="w-full resize-none border border-slate-300 rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent break-words whitespace-pre-wrap overflow-wrap-anywhere"
                style={{ minHeight: '200px', height: '30vh' }}
                autoFocus
              />
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSaveAndRegenerate}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 active:bg-green-100 focus:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition-colors font-medium text-sm px-3 py-1 rounded"
                >
                  Save and Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetail; 